/* Small controls shared by the top bar and the panels. */

import { useState } from 'react';
import { formatMoney, type SheetChoice } from '@gsb/core';
import { useStore, setSheetChoice, setTransfer, flushSave } from '../store/store';
import { NumberField } from '../ui/primitives';
import { Icon } from '../ui/icons';

export function sheetChoiceValue(c: SheetChoice): string {
  return c.mode === 'fixed' ? 'fixed:' + c.key : c.mode === 'length' ? 'length:' + c.lengthMm : c.mode;
}

/** Sheet size: Auto (cheapest), by the metre, a fixed size, or a set length. */
export function SheetChoiceSelect({ bare, hint = true }: { bare?: boolean; hint?: boolean }) {
  const doc = useStore(s => s.doc);
  const cfg = useStore(s => s.config)!;
  const tp = cfg.pricing.transfers[doc.transfer];
  const [custom, setCustom] = useState(false);
  const v = sheetChoiceValue(doc.settings.sheet);
  const isCustom = doc.settings.sheet.mode === 'length' && !tp.byLength.presetsMm.includes(doc.settings.sheet.lengthMm);
  const select = (
    <select className={'in' + (bare ? ' in-sm' : '')} aria-label="Sheet size" title="Which sheet to print on. Auto picks the cheapest that fits." value={isCustom || custom ? 'custom' : v} onChange={e => {
      const x = e.target.value;
      setCustom(x === 'custom');
      if (x === 'auto' || x === 'roll') setSheetChoice({ mode: x });
      else if (x.startsWith('fixed:')) setSheetChoice({ mode: 'fixed', key: x.slice(6) });
      else if (x.startsWith('length:')) setSheetChoice({ mode: 'length', lengthMm: Number(x.slice(7)) });
      else if (x === 'custom') {
        setSheetChoice({ mode: 'length', lengthMm: doc.settings.sheet.mode === 'length' ? doc.settings.sheet.lengthMm : 1500 });
        if (bare) useStore.setState({ panel: 'nest' });
      }
    }}>
      <option value="auto">Auto: cheapest that fits</option>
      <option value="roll">By the metre: arrange it myself</option>
      <optgroup label="Fixed sheets">
        {tp.fixedSheets.map(f => <option key={f.key} value={'fixed:' + f.key}>{f.name} · {f.widthMm / 10} × {f.lengthMm / 10} cm · {formatMoney(f.price)}</option>)}
      </optgroup>
      <optgroup label={'By the metre (' + tp.byLength.widthMm / 10 + ' cm wide)'}>
        {tp.byLength.presetsMm.map(L => <option key={L} value={'length:' + L}>{L / 1000} m sheet</option>)}
        <option value="custom">Custom length…</option>
      </optgroup>
    </select>
  );
  if (bare) return select;
  return (
    <div className="field">
      <span className="label">Sheet size</span>
      {select}
      {(isCustom || custom) && doc.settings.sheet.mode === 'length' && (
        <div style={{ marginTop: 8 }}>
          <NumberField label="Length" unit="cm" mmValue value={doc.settings.sheet.lengthMm} min={tp.byLength.minLengthMm} max={tp.byLength.maxLengthMm}
            onCommit={x => setSheetChoice({ mode: 'length', lengthMm: Math.round(x) })} />
        </div>
      )}
      {hint && <span className="tiny muted" style={{ marginTop: 4 }}>{doc.settings.sheet.mode === 'auto' ? 'We compare every size and use the cheapest.' : doc.settings.sheet.mode === 'roll' ? 'You arrange the designs. The sheet starts at ' + tp.byLength.minLengthMm / 1000 + ' m and grows to fit, up to ' + tp.byLength.maxLengthMm / 1000 + ' m; more goes on another sheet.' : 'More sheets are added if everything doesn’t fit.'}</span>}
    </div>
  );
}

/** DTF / UV DTF */
export function TransferSeg({ size = 'sm' }: { size?: 'sm' | 'md' }) {
  const t = useStore(s => s.doc.transfer);
  const tp = useStore(s => s.config?.pricing.transfers);
  return (
    <div className={'tseg' + (size === 'md' ? ' tseg-md' : '')} role="group" aria-label="Transfer type">
      {(['dtf', 'uv'] as const).map(k => (
        <button key={k} type="button" data-t={k} aria-pressed={t === k} onClick={() => setTransfer(k)} title={tp?.[k].blurb}>
          {k === 'uv' ? 'UV DTF' : 'DTF'}
        </button>
      ))}
    </div>
  );
}

export function SaveStatus() {
  const save = useStore(s => s.save);
  const pid = useStore(s => s.projectId);
  const savedTo = useStore(s => (s.config?.email?.mode === 'on' ? s.meta.savedTo : null));
  if (!pid) return <span className="save is-idle">Not saved yet</span>;
  if (save.state === 'saving' || save.state === 'dirty') return <span className="save" aria-live="polite"><span className="spin" style={{ width: 12, height: 12, borderWidth: 2 }} /> Saving…</span>;
  if (save.state === 'error') return <button className="save is-error" onClick={() => void flushSave()} title={save.error}><Icon name="alert" /> Not saved · retry</button>;
  if (save.state === 'conflict') return <span className="save is-error"><Icon name="alert" /> Changed elsewhere</span>;
  // saved to an email (0.3e Stage B): say so, with the address on hover
  if (savedTo) return <span className="save is-saved is-email" aria-live="polite" title={'Saved to ' + savedTo + '. Open it on any device with Find My Gangsheets.'}><Icon name="mail" /> Saved to email</span>;
  return <span className="save is-saved" aria-live="polite"><Icon name="check" /> Saved</span>;
}
